import { useLanguage } from '@/contexts/language-context';
import { formatDate } from '@/routes/jobs/helpers';
import { formatMoney } from '@/lib/utils';

import { formatDateTime, invoicePhase, invoicePhaseColorClass, invoicePhaseLabel } from './helpers';
import type { Invoice } from './types';

interface Props {
  invoice: Invoice;
  /** Label for `invoice.payment_method_id`, resolved by the caller once it has the business's payment methods loaded. */
  paidViaLabel?: string | null;
}

// Read-only, invoice-style rendering — the "actual invoice" visual, so it can
// be reviewed without a stored PDF. Mirrors `routes/quotes/quote-document.tsx`.
export function InvoiceDocument({ invoice, paidViaLabel }: Props) {
  const { t } = useLanguage();
  const phase = invoicePhase(invoice);

  const meta: string[] = [`${t.invoiceCreatedAt} ${formatDateTime(invoice.created_at)}`];
  if (invoice.sent_at) meta.push(`${t.invoiceSentAt} ${formatDateTime(invoice.sent_at)}`);
  if (invoice.due_date) meta.push(`${t.invoiceDueDate} ${formatDate(invoice.due_date)}`);
  if (invoice.paid_at) meta.push(`${t.invoicePaidAt} ${formatDateTime(invoice.paid_at)}`);

  return (
    <div
      data-testid="invoice-document"
      className="rounded-lg border bg-background p-5 flex flex-col gap-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold leading-tight">{t.invoicesTitle}</h3>
          {invoice.job_title && (
            <p className="text-sm text-muted-foreground truncate">{invoice.job_title}</p>
          )}
        </div>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${invoicePhaseColorClass(phase)}`}
          data-testid="invoice-phase"
        >
          {invoicePhaseLabel(phase, t)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground -mt-3">{meta.join(' · ')}</p>

      {/* Amounts */}
      <div className="flex flex-col gap-1 text-sm">
        {invoice.subtotal != null && (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{t.invoiceSubtotal}</span>
            <span className="tabular-nums">{formatMoney(invoice.subtotal)}</span>
          </div>
        )}
        {invoice.tax != null && (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{t.invoiceTax}</span>
            <span className="tabular-nums">{formatMoney(invoice.tax)}</span>
          </div>
        )}
        {invoice.discount != null && invoice.discount !== 0 && (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>{t.invoiceDiscount}</span>
            <span className="tabular-nums">-{formatMoney(invoice.discount)}</span>
          </div>
        )}
        <div className="flex items-center justify-between font-semibold border-t pt-1.5 mt-1">
          <span>{t.invoiceTotal}</span>
          <span className="tabular-nums" data-testid="invoice-doc-total">
            {formatMoney(invoice.total_amount)}
          </span>
        </div>
      </div>

      {/* Payment provenance */}
      {(invoice.payment_claimed_by_name || paidViaLabel) && (
        <div className="flex flex-col gap-1 border-t pt-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t.invoicePaymentMethod}
          </p>
          <p className="text-sm" data-testid="invoice-doc-payment-provenance">
            {[paidViaLabel, invoice.payment_claimed_by_name].filter(Boolean).join(' — ')}
          </p>
        </div>
      )}
    </div>
  );
}
